// Mariea Connect — mobile PWA for Dr. Mariea's Publication and Research International Pvt. Ltd.
// Client-only. Reuses the /api/client/* endpoints and the existing client_token.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PhaseTracker from "@/components/app/PhaseTracker";
import VerticalPhaseTracker from "@/components/app/VerticalPhaseTracker";
import TracApproval, { TracApprovalChip } from "@/components/app/TracApproval";
import { StatusBadge } from "@/lib/statusColor";
import {
  Home as HomeIcon, FolderKanban, MessageSquare, FileText,
  LogOut, ChevronRight, ChevronLeft, Download, Send, Share2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const trac = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });
trac.interceptors.request.use((c) => {
  const t = localStorage.getItem("client_token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const NAVY = "#1B2A4A";
const NAVY_DARK = "#12203A";
const GOLD = "#C9A44A";
const CREAM = "#F7F5EF";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

/* ---------------- Login ---------------- */
export function TracLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.title = "Mariea Connect — Sign in"; }, []);
  // If already signed in, skip to home
  useEffect(() => {
    if (localStorage.getItem("client_token")) {
      trac.get("/client/me").then(() => nav("/trac", { replace: true })).catch(() => {});
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await trac.post("/client/login", { email, password });
      localStorage.setItem("client_token", data.token);
      nav("/trac", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign in failed");
    }
    setBusy(false);
  };

  return (
    <div
      className="relative min-h-[100dvh] flex flex-col items-center md:items-end justify-between md:justify-center md:gap-6 px-5 md:pr-[7vw] pt-6 md:pt-10 pb-6 md:pb-8 bg-cover bg-[position:center_18%] md:bg-center"
      data-testid="trac-login"
      style={{ backgroundImage: "url(/brand/client-bg.jpg)" }}
    >
      <div aria-hidden className="fixed inset-0 pointer-events-none md:hidden"
        style={{ background: "linear-gradient(180deg, rgba(10,18,36,0.55) 0%, rgba(10,18,36,0.08) 26%, rgba(10,18,36,0.05) 42%, rgba(10,18,36,0.6) 68%, rgba(10,18,36,0.88) 100%)" }} />
      <div aria-hidden className="fixed inset-0 pointer-events-none hidden md:block"
        style={{ background: "linear-gradient(90deg, rgba(10,18,36,0.15) 0%, rgba(10,18,36,0.2) 45%, rgba(10,18,36,0.72) 100%)" }} />

      <div className="relative text-center md:w-full md:max-w-sm" data-testid="trac-login-brand">
        <img src="/brand/logo.png" alt="Dr. Mariea's" className="w-16 md:w-24 h-auto mx-auto select-none drop-shadow-lg" draggable={false} />
        <h1 data-testid="trac-app-name" className="font-display mt-2 md:mt-3 text-2xl md:text-4xl leading-none"
          style={{ color: "#F4E9CE", textShadow: "0 2px 14px rgba(10,18,36,0.75)" }}>Mariea Connect</h1>
        <div data-testid="trac-app-subtitle" className="mt-1.5 text-xs md:text-sm tracking-wide"
          style={{ color: "#F4E9CE", textShadow: "0 1px 10px rgba(10,18,36,0.8)" }}>
          Your Research. Your Progress. One Place.
        </div>
        <div className="hidden md:block font-display mt-3 text-sm leading-snug max-w-xs mx-auto"
          style={{ color: "rgba(244,233,206,0.85)", textShadow: "0 1px 10px rgba(10,18,36,0.65)" }}>
          Dr. Mariea's Publication and Research International Pvt. Ltd
        </div>
      </div>

      <div className="flex-1 min-h-[34dvh] md:hidden" aria-hidden />

      <form onSubmit={submit}
        className="relative w-full max-w-sm rounded-3xl px-6 pt-5 pb-6 md:pt-6 md:pb-7"
        data-testid="trac-login-form"
        style={{
          background: "rgba(255,255,255,0.94)", backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          border: `1px solid ${GOLD}66`,
          boxShadow: "0 24px 60px -18px rgba(10,18,36,0.65)",
        }}>
        <div className="label-caps mb-1" style={{ color: GOLD }} data-testid="trac-signin-label">Mariea Connect · Client Sign In</div>
        <h2 className="font-display text-2xl md:text-3xl mb-4 md:mb-5" style={{ color: NAVY }}>Welcome.</h2>

        <label className="label-caps block mb-1" style={{ color: NAVY }}>Email</label>
        <Input data-testid="trac-email" type="email" required inputMode="email" autoComplete="email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          className="rounded-lg h-11 mb-4 focus-visible:ring-0"
          style={{ borderColor: `${NAVY}40` }} />

        <label className="label-caps block mb-1" style={{ color: NAVY }}>Password</label>
        <Input data-testid="trac-password" type="password" required autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          className="rounded-lg h-11 focus-visible:ring-0"
          style={{ borderColor: `${NAVY}40` }} />

        {error && (
          <div data-testid="trac-login-error" className="mt-4 border border-[#B02A00] text-[#B02A00] px-3 py-2 text-sm rounded-lg">
            {error}
          </div>
        )}

        <button data-testid="trac-login-submit" disabled={busy}
          className="mt-6 w-full h-11 rounded-full font-semibold text-sm tracking-wide transition-all disabled:opacity-60 hover:brightness-110 active:scale-[0.99]"
          style={{ background: `linear-gradient(180deg, ${NAVY}, ${NAVY_DARK})`, color: "#F4E9CE",
            boxShadow: `0 10px 24px -10px rgba(18,32,58,0.6), inset 0 1px 0 ${GOLD}59` }}>
          {busy ? "Signing in…" : "Sign in →"}
        </button>
      </form>

      <div className="relative md:w-full md:max-w-sm text-center text-[11px] font-mono-tight uppercase tracking-widest mt-4 md:mt-0"
        style={{ color: "rgba(244,233,206,0.8)" }} data-testid="trac-login-footer">
        Mariea Connect · Mobile Client Portal
      </div>
    </div>
  );
}

/* ---------------- Shell with bottom nav ---------------- */
function AuthGuard({ children, onClient }) {
  const nav = useNavigate();
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    trac.get("/client/me")
      .then((r) => { onClient && onClient(r.data); setChecked(true); })
      .catch(() => nav("/trac/login", { replace: true }));
  }, [nav, onClient]);
  if (!checked) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center" style={{ background: CREAM }}>
        <div className="text-sm" style={{ color: `${NAVY}99` }}>Loading…</div>
      </div>
    );
  }
  return children;
}

function TracHeader({ client, title, back }) {
  const nav = useNavigate();
  const logout = async () => {
    try { await trac.post("/client/logout"); } catch {}
    localStorage.removeItem("client_token");
    nav("/trac/login", { replace: true });
  };
  return (
    <header
      className="sticky top-0 z-30 px-4 pt-[max(env(safe-area-inset-top),0.5rem)] pb-3"
      style={{ background: NAVY, color: "#F4E9CE", borderBottom: `1px solid ${GOLD}55` }}
      data-testid="trac-header"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {back ? (
            <button data-testid="trac-back" onClick={() => nav(-1)} className="p-1 -ml-1 rounded-full" aria-label="Back">
              <ChevronLeft size={22} />
            </button>
          ) : (
            <img src="/brand/logo.png" alt="Dr. Mariea's" className="w-8 h-8 object-contain" draggable={false} />
          )}
          <div className="min-w-0">
            <div className="label-caps truncate" style={{ color: `${GOLD}` }}>Mariea Connect</div>
            <div className="font-display text-lg leading-tight truncate" data-testid="trac-page-title">
              {title}
            </div>
          </div>
        </div>
        <button data-testid="trac-logout" onClick={logout}
          className="inline-flex items-center gap-1 text-[11px] font-mono-tight uppercase tracking-widest px-2.5 py-1.5 rounded-full"
          style={{ background: `${GOLD}22`, color: "#F4E9CE", border: `1px solid ${GOLD}66` }}>
          <LogOut size={12} /> Sign out
        </button>
      </div>
      {client?.name && (
        <div className="mt-2 text-[11px] font-mono-tight uppercase tracking-widest" style={{ color: `${GOLD}DD` }}>
          {client.name}{client.company_name ? ` · ${client.company_name}` : ""}
        </div>
      )}
    </header>
  );
}

const NAV = [
  { to: "/trac", icon: HomeIcon, label: "Home", end: true, testid: "nav-home" },
  { to: "/trac/projects", icon: FolderKanban, label: "Projects", testid: "nav-projects" },
  { to: "/trac/messages", icon: MessageSquare, label: "Messages", testid: "nav-messages" },
  { to: "/trac/files", icon: FileText, label: "Files", testid: "nav-files" },
];

function TracBottomNav() {
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-30"
      data-testid="trac-bottom-nav"
      style={{
        background: NAVY,
        borderTop: `1px solid ${GOLD}55`,
        paddingBottom: "max(env(safe-area-inset-bottom), 0.25rem)",
      }}
    >
      <ul className="max-w-md mx-auto grid grid-cols-4">
        {NAV.map(({ to, icon: Icon, label, end, testid }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-mono-tight uppercase tracking-widest transition-colors ${
                  isActive ? "" : "opacity-70 hover:opacity-100"
                }`
              }
              style={({ isActive }) => ({ color: isActive ? GOLD : "#F4E9CE" })}
            >
              <Icon size={18} strokeWidth={2} />
              {label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function TracShell() {
  const [client, setClient] = useState(null);
  const location = useLocation();

  // Register service worker once on mount.
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/trac-sw.js", { scope: "/trac" }).catch(() => {});
    }
    // Request notification permission (used for in-session message alerts)
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch {}
    }
  }, []);

  const back = /^\/trac\/(projects\/[^/]+|conversations\/[^/]+)$/.test(location.pathname);
  const titleMap = {
    "/trac": "Home",
    "/trac/projects": "Projects",
    "/trac/messages": "Messages",
    "/trac/files": "Files",
  };
  const title = useMemo(() => {
    if (location.pathname.startsWith("/trac/projects/")) return "Project";
    if (location.pathname.startsWith("/trac/conversations/")) return "Conversation";
    return titleMap[location.pathname] || "Mariea Connect";
  }, [location.pathname]);

  useEffect(() => { document.title = `Mariea Connect — ${title}`; }, [title]);

  return (
    <AuthGuard onClient={setClient}>
      <div className="min-h-[100dvh] flex flex-col" style={{ background: CREAM }}>
        <TracHeader client={client} title={title} back={back} />
        <main className="flex-1 max-w-md w-full mx-auto px-4 pt-4 pb-28">
          <Outlet context={{ client }} />
        </main>
        <TracBottomNav />
      </div>
    </AuthGuard>
  );
}

/* ---------------- Home ---------------- */
export function TracHome() {
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    trac.get("/client/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const active = (projects || []).filter((p) => (p.status || "").toLowerCase() !== "completed");

  return (
    <div data-testid="trac-home">
      <h1 className="font-display text-3xl leading-tight" style={{ color: NAVY }}>
        Your projects
      </h1>
      <p className="text-sm mt-1" style={{ color: `${NAVY}99` }}>
        {projects === null ? "Loading…" : active.length === 0 ? "No active projects right now." : `${active.length} active ${active.length === 1 ? "project" : "projects"}`}
      </p>

      <ul className="mt-5 space-y-3" data-testid="trac-home-project-list">
        {(projects || []).map((p) => <ProjectCard key={p.id} p={p} />)}
        {projects && projects.length === 0 && (
          <li className="text-center py-10 text-sm" style={{ color: `${NAVY}80` }}>
            Your editorial team hasn't assigned any projects yet.
          </li>
        )}
      </ul>
    </div>
  );
}

function ProjectCard({ p }) {
  const phases = p.phases || [];
  const today = new Date().toISOString().slice(0, 10);
  let active = phases.findIndex((ph) => (!ph.start_date || ph.start_date <= today) && (!ph.end_date || ph.end_date >= today));
  if (active === -1) active = Math.max(0, (phases.filter((ph) => ph.end_date && ph.end_date < today).length) - 1);
  const currentPhase = phases[active]?.name;

  return (
    <li>
      <Link to={`/trac/projects/${p.id}`} data-testid={`trac-project-${p.id}`}
        className="block rounded-2xl bg-white p-4 border transition-all active:scale-[0.995] hover:border-[color:var(--navy)]"
        style={{ borderColor: `${NAVY}1a` }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display text-lg truncate" style={{ color: NAVY }}>{p.name}</div>
            <div className="text-[11px] font-mono-tight uppercase tracking-widest mt-0.5" style={{ color: `${NAVY}80` }}>
              {p.project_number || "—"}
            </div>
          </div>
          <StatusBadge status={p.status} testid={`trac-project-status-${p.id}`} />
        </div>
        {p.client_approval_status && <div className="mt-2"><TracApprovalChip status={p.client_approval_status} testid={`trac-project-approval-${p.id}`} /></div>}
        {currentPhase && (
          <div className="mt-3 text-xs" style={{ color: `${NAVY}B3` }}>
            <span className="font-mono-tight uppercase tracking-widest" style={{ color: GOLD }}>Current phase · </span>
            {currentPhase}
          </div>
        )}
        <div className="mt-3 flex items-center justify-between text-xs" style={{ color: `${NAVY}80` }}>
          <span>Due {fmtDate(p.due_date)}</span>
          <ChevronRight size={14} />
        </div>
      </Link>
    </li>
  );
}

/* ---------------- Projects list ---------------- */
export function TracProjects() {
  const [projects, setProjects] = useState(null);
  useEffect(() => { trac.get("/client/projects").then((r) => setProjects(r.data)).catch(() => setProjects([])); }, []);
  return (
    <div data-testid="trac-projects">
      <ul className="space-y-3">
        {(projects || []).map((p) => <ProjectCard key={p.id} p={p} />)}
        {projects && projects.length === 0 && (
          <li className="text-center py-10 text-sm" style={{ color: `${NAVY}80` }}>No projects yet.</li>
        )}
      </ul>
    </div>
  );
}

/* ---------------- Project detail ---------------- */
export function TracProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [concerns, setConcerns] = useState([]);
  const [tab, setTab] = useState("overview"); // overview | files | messages

  const load = () => {
    trac.get(`/client/projects/${id}`).then((r) => setData(r.data)).catch(() => nav("/trac"));
    trac.get("/client/concerns").then((r) => setConcerns(r.data.filter((c) => c.project_id === id))).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="text-sm" style={{ color: `${NAVY}80` }}>Loading…</div>;
  const { project, work_orders } = data;
  const files = (project.files || []).filter((f) => !f.hidden);
  const managerName = project.manager_name || project.manager_email || "Your editorial manager";

  return (
    <div data-testid="trac-project-detail">
      <div className="rounded-2xl p-4" style={{ background: NAVY, color: "#F4E9CE", border: `1px solid ${GOLD}66` }}>
        <div className="label-caps" style={{ color: GOLD }}>{project.project_number || "Project"}</div>
        <div className="font-display text-2xl mt-1 leading-tight break-words [overflow-wrap:anywhere]">{project.name}</div>
        <div className="mt-3 flex items-center gap-2">
          <StatusBadge status={project.status} testid={`trac-detail-status-${project.id}`} />
          <span className="text-[11px] font-mono-tight uppercase tracking-widest opacity-80">Due {fmtDate(project.due_date)}</span>
        </div>
        <div className="mt-3 text-xs opacity-85">
          <span className="font-mono-tight uppercase tracking-widest" style={{ color: GOLD }}>Manager · </span>
          {managerName}
        </div>
      </div>

      <div className="mt-4">
        {(project.phases || []).length > 0 ? (
          <VerticalPhaseTracker phases={project.phases} projectStatus={project.status} testid="trac-phase-tracker-vertical" />
        ) : (
          <PhaseTracker phases={[]} testid="trac-phase-tracker" emptyText="Your project timeline will appear here once your manager sets it up." />
        )}
      </div>

      <TracApproval api={trac} projectId={id} />

      {/* Tabs */}
      <div className="mt-5 grid grid-cols-3 rounded-full p-1" style={{ background: `${NAVY}10`, border: `1px solid ${NAVY}22` }}>
        {[
          ["overview", "Overview"],
          ["files", `Files (${files.length})`],
          ["messages", `Messages (${concerns.length})`],
        ].map(([k, lbl]) => (
          <button key={k} data-testid={`trac-tab-${k}`} onClick={() => setTab(k)}
            className="text-[11px] font-mono-tight uppercase tracking-widest py-2 rounded-full transition-all"
            style={{ background: tab === k ? NAVY : "transparent", color: tab === k ? "#F4E9CE" : NAVY }}>
            {lbl}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {tab === "overview" && <OverviewTab wos={work_orders} />}
        {tab === "files" && <FilesTab files={files} />}
        {tab === "messages" && <MessagesTab projectId={id} concerns={concerns} onPosted={load} />}
      </div>
    </div>
  );
}

function OverviewTab({ wos }) {
  return (
    <ul className="space-y-2" data-testid="trac-overview-wos">
      {wos.map((w) => (
        <li key={w.id} className="bg-white rounded-xl p-3 flex items-center justify-between gap-3"
          style={{ border: `1px solid ${NAVY}1a` }}>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: NAVY }}>{w.title}</div>
            <div className="text-[11px] font-mono-tight uppercase tracking-widest mt-0.5" style={{ color: `${NAVY}80` }}>
              Due {fmtDate(w.deadline)}
            </div>
          </div>
          <StatusBadge status={w.status} />
        </li>
      ))}
      {wos.length === 0 && <li className="text-sm text-center py-6" style={{ color: `${NAVY}80` }}>No tasks yet.</li>}
    </ul>
  );
}

function FilesTab({ files }) {
  if (files.length === 0) {
    return <div className="text-sm text-center py-8" style={{ color: `${NAVY}80` }}>No files shared on this project yet.</div>;
  }
  return (
    <ul className="space-y-2" data-testid="trac-files-list">
      {files.map((f) => (
        <li key={f.id || f.url} className="bg-white rounded-xl p-3 flex items-center justify-between gap-3"
          style={{ border: `1px solid ${NAVY}1a` }}>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: NAVY }}>{f.name || "Attachment"}</div>
            <div className="text-[11px] font-mono-tight uppercase tracking-widest mt-0.5" style={{ color: `${NAVY}80` }}>
              {f.uploaded_at ? fmtDate(f.uploaded_at) : (f.created_at ? fmtDate(f.created_at) : "")}
            </div>
          </div>
          {f.url && (
            <a data-testid={`trac-file-dl-${f.id || "x"}`} href={f.url} target="_blank" rel="noreferrer download"
              className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{ background: NAVY, color: "#F4E9CE" }}>
              <Download size={13} /> Open
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

function MessagesTab({ projectId, concerns, onPosted }) {
  const [form, setForm] = useState({ subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) return;
    setBusy(true);
    try {
      await trac.post("/client/concerns", { project_id: projectId, ...form });
      setForm({ subject: "", body: "" });
      toast.success("Message sent to your manager");
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Mariea Connect", { body: "Your message has been sent.", icon: "/brand/icon-192.png" });
      }
      onPosted && onPosted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    }
    setBusy(false);
  };
  return (
    <div data-testid="trac-messages-tab">
      <ul className="space-y-2">
        {concerns.map((c) => (
          <li key={c.id} className="bg-white rounded-xl p-3" style={{ border: `1px solid ${NAVY}1a` }}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-sm truncate" style={{ color: NAVY }}>{c.subject}</div>
              <StatusBadge status={c.status} />
            </div>
            <div className="text-sm mt-1 whitespace-pre-wrap" style={{ color: `${NAVY}CC` }}>{c.body}</div>
            <div className="text-[11px] font-mono-tight uppercase tracking-widest mt-1" style={{ color: `${NAVY}70` }}>{fmtDate(c.created_at)}</div>
            {c.response && (
              <div className="mt-2 rounded-lg p-2" style={{ background: `${GOLD}1a`, borderLeft: `3px solid ${GOLD}` }}>
                <div className="text-[11px] font-mono-tight uppercase tracking-widest" style={{ color: NAVY }}>
                  {c.responded_by || "Studio"} replied · {c.responded_at ? fmtDate(c.responded_at) : ""}
                </div>
                <div className="text-sm mt-1 whitespace-pre-wrap" style={{ color: NAVY }}>{c.response}</div>
              </div>
            )}
          </li>
        ))}
        {concerns.length === 0 && <li className="text-sm text-center py-6" style={{ color: `${NAVY}80` }}>No messages yet. Say hello below.</li>}
      </ul>

      <form onSubmit={submit} className="mt-4 bg-white rounded-xl p-3 space-y-2" style={{ border: `1px solid ${NAVY}1a` }} data-testid="trac-message-form">
        <Input data-testid="trac-message-subject" required placeholder="Subject" value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          className="rounded-lg h-10 focus-visible:ring-0" style={{ borderColor: `${NAVY}40` }} />
        <Textarea data-testid="trac-message-body" required rows={3} placeholder="Write a message to your manager…"
          value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
          className="rounded-lg focus-visible:ring-0" style={{ borderColor: `${NAVY}40` }} />
        <button data-testid="trac-message-send" disabled={busy}
          className="w-full h-10 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: NAVY, color: "#F4E9CE" }}>
          <Send size={14} /> {busy ? "Sending…" : "Send message"}
        </button>
      </form>
    </div>
  );
}

/* ---------------- Messages (all projects) ---------------- */
export function TracMessages() {
  const [concerns, setConcerns] = useState(null);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    trac.get("/client/concerns").then((r) => setConcerns(r.data)).catch(() => setConcerns([]));
    trac.get("/client/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);
  const pmap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  if (concerns === null) return <div className="text-sm" style={{ color: `${NAVY}80` }}>Loading…</div>;
  if (concerns.length === 0) return (
    <div className="text-center py-10 text-sm" style={{ color: `${NAVY}80` }} data-testid="trac-messages-empty">
      No messages yet. Open a project and tap <span className="font-semibold">Messages</span> to start a conversation with your manager.
    </div>
  );
  return (
    <ul className="space-y-2" data-testid="trac-messages-list">
      {concerns.map((c) => (
        <Link key={c.id} to={`/trac/projects/${c.project_id}`}
          className="block bg-white rounded-xl p-3" style={{ border: `1px solid ${NAVY}1a` }}
          data-testid={`trac-message-item-${c.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold text-sm truncate" style={{ color: NAVY }}>{c.subject}</div>
            <StatusBadge status={c.status} />
          </div>
          <div className="text-xs mt-0.5" style={{ color: `${NAVY}80` }}>
            {pmap[c.project_id]?.name || "Project"} · {fmtDate(c.created_at)}
          </div>
          {c.response && (
            <div className="text-xs mt-2 truncate" style={{ color: `${NAVY}CC` }}>
              <span className="font-mono-tight uppercase tracking-widest" style={{ color: GOLD }}>Reply · </span>
              {c.response}
            </div>
          )}
        </Link>
      ))}
    </ul>
  );
}

/* ---------------- Files (all projects) ---------------- */
export function TracFiles() {
  const [projects, setProjects] = useState(null);
  const [projectFiles, setProjectFiles] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { data: list } = await trac.get("/client/projects");
        setProjects(list);
        const entries = await Promise.all(
          list.map(async (p) => {
            try {
              const { data } = await trac.get(`/client/projects/${p.id}`);
              return [p.id, (data.project.files || []).filter((f) => !f.hidden)];
            } catch { return [p.id, []]; }
          })
        );
        setProjectFiles(Object.fromEntries(entries));
      } catch { setProjects([]); }
    })();
  }, []);
  if (projects === null) return <div className="text-sm" style={{ color: `${NAVY}80` }}>Loading…</div>;
  const totalFiles = Object.values(projectFiles).reduce((n, arr) => n + arr.length, 0);
  if (totalFiles === 0) return (
    <div className="text-center py-10 text-sm" style={{ color: `${NAVY}80` }} data-testid="trac-files-empty">
      No files shared yet.
    </div>
  );
  return (
    <div className="space-y-5" data-testid="trac-files-all">
      {projects.map((p) => {
        const files = projectFiles[p.id] || [];
        if (files.length === 0) return null;
        return (
          <section key={p.id}>
            <div className="label-caps mb-2" style={{ color: NAVY }}>
              {p.project_number ? `${p.project_number} · ` : ""}{p.name}
            </div>
            <FilesTab files={files} />
          </section>
        );
      })}
    </div>
  );
}
